import {
  AfterViewInit,
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  Renderer2,
  ChangeDetectionStrategy
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { Router } from '@angular/router';
import { tap } from 'rxjs/operators';
import { AppService } from '../../app.service';
import { UtilsService } from '../../shared/utils.service';
import { RBACService } from '../../shared/rbac.service';
import { CustomDataSource } from '../../utils/custom-data-source';
import { HazardModel } from 'src/app/models/hazard.model';
import { HazardsEditComponent } from '../hazards-edit/hazards-edit.component';
import { HazardsViewComponent } from '../hazards-view/hazards-view.component';
import { ApiRequestService } from 'src/app/shared/api-request.service';
import { HazardsFilterComponent } from "../hazards-filter/hazards-filter.component";
import { HazardEditPermissions } from '../../interfaces/hazard-edit-permissions.interface';
import {ChartsUtilsService} from "../../charts/charts-utils.service";
import {
  AccountSitesHazardsSelectorComponent
} from "../../shared/account-sites-hazards-selector/account-sites-hazards-selector.component";

@Component({
    selector: 'app-hazards',
    templateUrl: './hazards.component.html',
    styleUrls: ['./hazards.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class HazardsComponent implements OnInit, AfterViewInit {
  // columns to show and the data source
  displayedColumns: string[] = [];
  dataSource = new HazardsDataSource(this.app, this.api);

  // the paginator and sorter
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  @ViewChild('fileInput') fileInput: ElementRef;

  // RBAC permission flags. Resolved once in ngOnInit() so the template does not
  // re-evaluate rbac.userCanAccess() on every change detection cycle.
  canView: boolean = false;
  canCreate: boolean = false;
  canEdit: boolean = false;
  canDelete: boolean = false;
  canViewSiteHazards: boolean = false;

  constructor(
    // public service: HazardsService,
    public utils: UtilsService,
    public app: AppService,
    public renderer: Renderer2,
    private dialog: MatDialog,
    private api: ApiRequestService,
    public chartsUtils: ChartsUtilsService,
    private router: Router,
    public rbac: RBACService
  ) { }

  ngOnInit() {
    // Resolve the RBAC permission flags before anything else so they are always set.
    this.canView = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_HAZARDS_RISKS, RBACService.RBAC_A.A_VIEW)
    ], true);
    this.canCreate = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_HAZARDS_RISKS, RBACService.RBAC_A.A_CREATE)
    ], true);
    this.canEdit = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_HAZARDS_RISKS, RBACService.RBAC_A.A_EDIT)
    ], true);
    this.canDelete = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_HAZARDS_RISKS, RBACService.RBAC_A.A_DELETE)
    ], true);
    this.canViewSiteHazards = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_SITES_HAZARDS_RISKS, RBACService.RBAC_A.A_VIEW)
    ], true);

    // Check if user has ANY hazard permission: redirect if none
    if (!this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_HAZARDS_RISKS, RBACService.RBAC_A.A_VIEW),
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_HAZARDS_RISKS, RBACService.RBAC_A.A_CREATE)
    ], false)) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to access hazards/risks.');
      this.router.navigate(['/dashboard']);
      return;
    }

    // Build displayedColumns dynamically
    this.displayedColumns = [
      'id',
      'site',
      'name',
      'status',
      'ira_score',
      'ira',
      'rra_score',
      'rra',
      'controls',
      'date_created',
      'reviewer_date',
      'actions'
    ];

    // Add select column if user has view permission (needed for export selection)
    if (this.canView) {
      this.displayedColumns.unshift('select');
      this.dataSource.getData();
    }
  }

  ngAfterViewInit() {
    // The table, its matSort and its mat-paginator all live inside a container gated by
    // *ngIf="canView", so without that permission Angular never creates those view
    // children and this.sort / this.paginator stay undefined. Bail out before wiring them up.
    if (!this.canView) {
      return;
    }

    // reset the paginator when sorting
    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));

    const _tap = tap(() => {
      this.dataSource.limit = this.paginator.pageSize;
      this.dataSource.offset = this.paginator.pageIndex;
      this.dataSource.sort_order = this.sort.direction;

      // sorting for utc time by parsing original time
      if (this.sort.active === "date_created_UTC") {
        this.dataSource.sort_by = "date_created";
      } else {
        this.dataSource.sort_by = this.sort.active;
      }

      this.dataSource.getData();
    });

    // subscribe to the paginator tap events
    this.paginator.page.pipe(_tap).subscribe();
    this.sort.sortChange.pipe(_tap).subscribe();
  }

  onAdd() {
    if (!this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_HAZARDS_RISKS, RBACService.RBAC_A.A_CREATE)
    ], true)) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to create hazards/risks.');
      return;
    }
    this.openHazardDialog(new HazardModel());
  }

  onEdit(hazard: HazardModel) {
    if (!this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_HAZARDS_RISKS, RBACService.RBAC_A.A_EDIT)
    ], true)) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to edit hazards/risks.');
      return;
    }
    this.openHazardDialog(hazard);
  }

  private openHazardDialog(hazard: HazardModel) {
    const permissions: HazardEditPermissions = {
      canSave: this.rbac.userCanAccess([
        RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_HAZARDS_RISKS, RBACService.RBAC_A.A_CREATE)
      ], true) || this.rbac.userCanAccess([
        RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_HAZARDS_RISKS, RBACService.RBAC_A.A_EDIT)
      ], true),
      canDelete: this.rbac.userCanAccess([
        RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_HAZARDS_RISKS, RBACService.RBAC_A.A_DELETE)
      ], true),
      canCopySites: true,
      canSelectReviewer: true
    };
    this.dialog
      .open(HazardsEditComponent, {
        width: '1024px',
        data: { ...hazard, _permissions: permissions }
      })
      .afterClosed()
      .subscribe((success) => {
        // Only refresh list if user has view permission
        if (this.rbac.userCanAccess([
          RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_HAZARDS_RISKS, RBACService.RBAC_A.A_VIEW)
        ], true)) {
          this.dataSource.getData(true);
        }
      });
  }

  onView(hazard: HazardModel) {
    this.dialog
      .open(HazardsViewComponent, {
        data: hazard
      })
      .afterClosed()
      .subscribe((success) => {
        if (!success) { return; }
        this.dataSource.getData(true);
      });
  }

  onClearSearch() {
    this.dataSource.search = '';
    this.dataSource.getData(true);
  }

  onRemove(id: number) {
    if (!this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_HAZARDS_RISKS, RBACService.RBAC_A.A_DELETE)
    ], true)) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to delete hazards/risks.');
      return;
    }
    this.utils.showModal(
      'Archive Hazard/Risk',
      'Are you sure you want to archive this Hazard/Risk template?',
      () => {
        this.api.makeRequest(
          'delete',
          'v1/hazards/' + id
        )
        .then((response) => {
          this.utils.showToast('The Hazard/Risk template was archived.');
          this.dataSource.selection.deselect(id);
          this.dataSource.getData();
        })
        .catch((error) => {
          this.utils.showModal('Error', error.error?.message);
        });
      }
    );
  }

  onRestore(id: number) {
    // Restoring is the inverse of archiving, so it is governed by the delete permission (as the API).
    if (!this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to restore hazards/risks.');
      return;
    }
    this.utils.showModal(
      'Restore Hazard/Risk template',
      'Are you sure you want to restore this Hazard/Risk template?',
      () => {
        this.api.makeRequest(
          'put',
          'v1/hazards/' + id + '/restore'
        )
        .then((response) => {
          this.utils.showToast('The Hazard/Risk template was restored.');
          this.dataSource.selection.deselect(id);
          this.dataSource.getData();
        })
        .catch((error) => {
          this.utils.showModal('Error', error.error?.message);
        });

      }
    );
  }

  onDeleteSelected() {
    if (!this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_HAZARDS_RISKS, RBACService.RBAC_A.A_DELETE)
    ], true)) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to delete hazards/risks.');
      return;
    }
    this.utils.showModal(
      'Archive Hazard & Risk Templates',
      'Are you sure you want to archive the selected Hazard & Risk templates?',
      () => {
        this.api.makeRequest(
          'delete',
          'v1/hazards/' + this.dataSource.selection.selected.join(',')
        )
        .then((response) => {
          this.utils.showToast('The selected Hazard & Risk templates was archived.');
          this.dataSource.selection.clear();
          this.dataSource.getData();
        })
        .catch((error) => {
          this.utils.showModal('Error', error.error?.message);
        });
      }
    );
  }

  onRestoreSelected() {
    // Restoring is the inverse of archiving, so it is governed by the delete permission (as the API).
    if (!this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to restore hazards/risks.');
      return;
    }
    this.utils.showModal(
      'Restore Selected Hazard & Risk templates',
      'Are you sure you want to restore the selected Hazard & Risk templates?',
      () => {
        this.api.makeRequest(
          'put',
          'v1/hazards/' + this.dataSource.selection.selected.join(',') + '/restore'
        )
        .then((response) => {
          this.utils.showToast('The selected Hazard & Risk templates was restored.');
          this.dataSource.selection.clear();
          this.dataSource.getData();
        })
        .catch((error) => {
          this.utils.showModal('Error', error.error?.message);
        });

      }
    );
  }

  /**
   * Exports the list of hazards into the specified format and sends a download request to the browser.
   * @param type csv, xls, xlsx, pdf.
   * Last updated by Kobus Beets on 13/1/2021.
   */
  onExportSelected(type: string = 'xlsx') {
    if (!this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_HAZARDS_RISKS, RBACService.RBAC_A.A_VIEW)
    ], true)) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to export hazards/risks.');
      return;
    }
    this.api.makeDownloadRequest(`v1/hazards/export/${type}` + (this.dataSource.selection.selected.length ? '/' + this.dataSource.selection.selected.join(',') : ''), {}, {
      site_ids: this.dataSource.site_ids.join(','),
      order_by: this.dataSource.sort_by,
      order: this.dataSource.sort_order
    })
      .then((response) => {
        // handle internet explorer - No longer supported
        // if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        //   window.navigator.msSaveOrOpenBlob(response);
        //   return;
        // }

        // Get the current date object
        const date = new Date();

        // Create object url to handle file downloads
        const data = window.URL.createObjectURL(response);

        // Create a download link
        const downloadLink = document.createElement('a');
        downloadLink.href = data;
        downloadLink.download = `hazards-risks-${date.getFullYear()}${(date.getMonth() + 1)}${date.getDate()}.${type}`;
        // Initiate the download
        downloadLink.click();

        // For Firefox it is necessary to delay revoking the ObjectURL
        setTimeout(function () {
          window.URL.revokeObjectURL(data);
        }, 300); // Minimum 300 miliseconds
      })
      .catch((errorResponse) => {
        this.utils.showModal('Error', errorResponse.message);
      });
  }

  onTriggerFileSelector(evt: Event) {
    if(this.fileInput) {
      const clickEvt: MouseEvent = new MouseEvent('click', { bubbles: true });
      this.fileInput.nativeElement.dispatchEvent(clickEvt);
    }
  }

  onImport(evt: any) {
    if (!this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_HAZARDS_RISKS, RBACService.RBAC_A.A_CREATE)
    ], true)) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to import hazards/risks.');
      return;
    }
    if (evt.target.files.length > 0) {
      const files: any = [];
      for (let i = 0; i < evt.target.files.length; i++) {
        files.push(evt.target.files[i]);
      }

      this.utils.showModal('Import Hazard & Risk Templates', 'Are you sure you want to import the Hazard & Risk templates from the selected files? It is recommended to use the same format as the Hazard & Risk templates exports. You will need to remove any duplicated Hazard & Risk templates from the list after importing.', () => {
        // Build the upload form preserving the original field naming (each file keyed by its name).
        const form = new FormData();
        files.forEach((file: File) => {
          form.append(file.name, file);
        });
        this.api.makeRequest('post', 'v1/hazards/import', form).then((response) => {
          this.utils.showToast('Import completed successfully.');
          this.dataSource.getData(true);
        });
      })

      // clear the target value
      evt.target.value = '';
    }
  }

  onCreateFrom(hazard: HazardModel) {
    if (!this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_HAZARDS_RISKS, RBACService.RBAC_A.A_CREATE)
    ], true)) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to create hazards/risks.');
      return;
    }
    const newHazard: HazardModel = new HazardModel();
    newHazard.createFrom(hazard);
    this.openHazardDialog(newHazard);
  }

  onFilter() {
    this.utils.showComponentDialog(
      HazardsFilterComponent,
      {
        site_ids: this.dataSource.site_ids,
        archived: this.dataSource.archived
      },
      {
        width: '500px'
      },
      (results) => {
        if (typeof results !== 'undefined') {
          this.dataSource.site_ids = results.site_ids ? results.site_ids : this.dataSource.site_ids;
          this.dataSource.archived = results.archived ? results.archived : this.dataSource.archived;
          this.dataSource.getData();
        }
      }
    );
  }

  onSelectHazards() {
    if (!this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_HAZARDS_RISKS, RBACService.RBAC_A.A_CREATE)
    ], true)) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to create hazards/risks.');
      return;
    }
    this.utils.showComponentDialog(
      AccountSitesHazardsSelectorComponent,
      {
        selected: [],
        multiple: true,
        selectedAccountId: this.app.account.id,
        site_id: 0
      },
      {
        width: '1200px'
      },
      (results: number[]) => {
        if (!results) { return; }
        if(results.length > 0) {
          this.onCopySiteHazards(results.join(','));
        }
      }
    );
  }

  onCopySiteHazards(selected_hazard_ids) {
    if (!this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_HAZARDS_RISKS, RBACService.RBAC_A.A_CREATE)
    ], true)) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to create hazards/risks.');
      return;
    }
    this.api.makeRequest('put', `v2/hazards/templates/copy-site-hazards`, [], {
      selected_hazard_ids: selected_hazard_ids
    })
      .then((response) => {
        this.dataSource.getData(true);
        this.utils.showToast(this.utils.getLangTerm('parent-child-sites-combined.singular', 'Site') + ' Hazards were copied to Templates.');
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  /**
   * Retrieves the site information from the provided hazard object.
   *
   * @param {any} hazard - The hazard object containing site information.
   * @return {any|null} The site information from the hazard object, or null if not found.
   */
  getHazardSite(hazard: any): any | null {
    return hazard.site || hazard.asset?.site || null;
  }

  // Expose RBACService to template
  protected readonly RBACService = RBACService;
}

/**
 * the data source class should contain everything needed to get data for the feature.
 * this only applies to lists. forms will use direct calls to the api.
 */
export class HazardsDataSource extends CustomDataSource {
  // Record sorting and direction
  sort_by: string = 'name';
  sort_order: string = 'asc';

  site_ids: number[] = [];
  archived = 'false';

  getData(resetOffset: boolean = false) {
    // this.getDataFromAPI('hazards/' + this.app.activeSiteId, resetOffset);
    this.getDataFromLaravelAPI(
      'hazards' +
      (this.site_ids
        ? '?site_ids=' + this.site_ids.join(',')
        : ''),
      resetOffset,
      () => { },
      {
        eager_load: true,
        archived: this.archived ? this.archived : 'false'
      }
    );
  }
}
